import { useEffect, useState } from 'react';
import { useIntegrationApp } from '@integration-app/react';
import { SelectBox } from 'src/components/SelectBox';
import { Option } from 'src/components/SelectBox';
import { ActionKey } from 'src/constants';
import { IntegrationKey } from 'src/services/integration-app.service';
import { useCrmSync } from '../../contexts/CrmSyncContext';
import { useCampaignContext } from '../../contexts/CampaignContext';
import { useExtraStepConfiguration } from '../FieldMapping/ExtraStepConfiguration';

export interface MondayExtraConfig {
  boardId?: string;
  groupId?: string | null;
  boardFields?: Option[];
}

interface MondayBoard {
  id: string;
  name: string;
  fields?: Record<string, string>;
}

interface MondayGroup {
  id: string;
  name: string;
}

export const MondayBoardSelector = () => {
  const [boards, setBoards] = useState<Option[]>([]);
  const [boardsData, setBoardsData] = useState<MondayBoard[]>([]);
  const [groups, setGroups] = useState<Option[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const integrationApp = useIntegrationApp();

  // Get values from context instead of props
  const { additionalParams, setAdditionalParams, syncInProgress } =
    useCrmSync();
  const { isInitializing } = useCampaignContext();
  const { setIsConfigurationComplete } = useExtraStepConfiguration();
  const disabled = syncInProgress || isInitializing;

  useEffect(() => {
    const loadBoards = async () => {
      setLoadingBoards(true);
      try {
        const result = await integrationApp
          .connection(IntegrationKey.MONDAY)
          .action(ActionKey.LIST_BOARDS)
          .run('{}');

        const boardsWithFields: MondayBoard[] = result.output.records || [];
        setBoardsData(boardsWithFields);
      } catch (error) {
        console.error('Failed to load Monday.com boards:', error);
        setBoardsData([]);
      } finally {
        setLoadingBoards(false);
      }
    };

    loadBoards();
  }, []);

  // Update boards whenever boardsData changes
  useEffect(() => {
    const boardOptions: Option[] = boardsData.map((board: MondayBoard) => ({
      value: board.id,
      label: board.name,
    }));

    setBoards(boardOptions);
  }, [boardsData]);

  useEffect(() => {
    const loadGroupsAndFields = async () => {
      if (!additionalParams.boardId) {
        setGroups([]);
        setAdditionalParams((prev) => ({
          ...prev,
          boardFields: [],
        }));
        return;
      }

      setLoadingGroups(true);
      try {
        // Load groups
        const groupsResult = await integrationApp
          .connection(IntegrationKey.MONDAY)
          .action(ActionKey.LIST_GROUPS)
          .run({ board_id: additionalParams.boardId });

        const groupOptions: Option[] =
          groupsResult.output.records?.map((group: MondayGroup) => ({
            value: group.id,
            label: group.name,
          })) || [];

        setGroups(groupOptions);
      } catch (error) {
        console.error('Failed to load Monday.com groups:', error);
        setGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    };

    loadGroupsAndFields();
  }, [additionalParams.boardId]);

  // Separate useEffect for handling field extraction
  useEffect(() => {
    if (!additionalParams.boardId || !boardsData.length) {
      return;
    }

    // Find the selected board and extract its fields
    const selectedBoardData = boardsData.find(
      (board) => board.id === additionalParams.boardId,
    );
    if (selectedBoardData && selectedBoardData.fields) {
      const fields: Option[] = Object.keys(selectedBoardData.fields).map(
        (fieldName) => ({
          value: selectedBoardData.fields![fieldName],
          label: fieldName,
        }),
      );
      setAdditionalParams((prev) => ({
        ...prev,
        boardFields: fields,
      }));
    }
  }, [boardsData]);

  useEffect(() => {
    const isComplete = !!(additionalParams.boardId && additionalParams.groupId);
    setIsConfigurationComplete(isComplete);
  }, [additionalParams.boardId, additionalParams.groupId]);

  const handleBoardChange = (value: string) => {
    setAdditionalParams((prev) => ({
      ...prev,
      boardId: value,
      groupId: null,
      boardFields: [],
    }));
  };

  const handleGroupChange = (value: string) => {
    setAdditionalParams((prev) => ({
      ...prev,
      groupId: value || null,
    }));
  };

  return (
    <div className="m-6 bg-neutral-0 border border-neutral-200 rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">Select Monday.com Board</h3>
        <p className="text-sm text-neutral-600 mb-4">
          Choose the board from which you want to filter contacts.
        </p>

        <SelectBox
          options={boards}
          value={
            boards.find(
              (board) => board.value === (additionalParams.boardId as string),
            ) || null
          }
          onChange={handleBoardChange}
          variant="outlined"
          size="medium"
          disabled={disabled || loadingBoards || boards.length === 0}
        />

        {!loadingBoards && boards.length === 0 && (
          <p className="text-sm text-error mt-2">
            No boards found. Please check your Monday.com connection.
          </p>
        )}
      </div>

      {(additionalParams.boardId as string) && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Select Group</h3>
          <p className="text-sm text-neutral-600 mb-4">
            Choose a specific group within the board to filter contacts further.
          </p>

          <SelectBox
            options={groups}
            value={
              groups.find(
                (group) => group.value === (additionalParams.groupId as string),
              ) || null
            }
            onChange={handleGroupChange}
            variant="outlined"
            size="medium"
            disabled={disabled || loadingGroups || groups.length === 0}
          />

          {loadingGroups && (
            <p className="text-sm text-neutral-600 mt-2">Loading groups...</p>
          )}

          {!loadingGroups &&
            groups.length === 0 &&
            (additionalParams.boardId as string) && (
              <p className="text-sm text-error mt-2">
                No groups found for this board.
              </p>
            )}
        </div>
      )}
    </div>
  );
};
